import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { CustomFieldDefinitionService } from 'brain-data/service/custom-field/custom-field-definition.service';
import {
  createCustomFieldDefinition,
  createCustomFieldDefinitionError,
  createCustomFieldDefinitionSuccess,
  deleteCustomFieldDefinition,
  deleteCustomFieldDefinitionError,
  deleteCustomFieldDefinitionSuccess,
  loadCustomFieldDefinitions,
  loadCustomFieldDefinitionsError,
  loadCustomFieldDefinitionsSuccess,
  updateCustomFieldDefinition,
  updateCustomFieldDefinitionError,
  updateCustomFieldDefinitionSuccess,
} from '../actions/custom-field-definition.action';

/**
 * AC9. The `HttpErrorResponse` reaches the reducer and the screen UNTOUCHED — the server's own
 * refusal text (`DEFINITION_KEY_IN_USE`, `DEFINITION_HAS_VALUES`, the immutability refusals) is what
 * the administrator must read, and restating it here in different words would put a claim about
 * server state on screen that this process cannot see.
 */
@Injectable()
export class CustomFieldDefinitionEffects {
  loadCustomFieldDefinitions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCustomFieldDefinitions),
      switchMap(({ appliesTo }) =>
        this.service.search(appliesTo).pipe(
          map(definitions => loadCustomFieldDefinitionsSuccess({ appliesTo, definitions })),
          catchError((error: HttpErrorResponse) => of(loadCustomFieldDefinitionsError({ error }))),
        ),
      ),
    ),
  );

  createCustomFieldDefinition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createCustomFieldDefinition),
      switchMap(({ payload }) =>
        this.service.create(payload).pipe(
          map(definition => createCustomFieldDefinitionSuccess({ definition })),
          catchError((error: HttpErrorResponse) => of(createCustomFieldDefinitionError({ error }))),
        ),
      ),
    ),
  );

  updateCustomFieldDefinition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCustomFieldDefinition),
      switchMap(({ id, payload }) =>
        this.service.update(id, payload).pipe(
          map(definition => updateCustomFieldDefinitionSuccess({ definition })),
          catchError((error: HttpErrorResponse) => of(updateCustomFieldDefinitionError({ error }))),
        ),
      ),
    ),
  );

  deleteCustomFieldDefinition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCustomFieldDefinition),
      switchMap(({ id }) =>
        this.service.delete(id).pipe(
          map(() => deleteCustomFieldDefinitionSuccess({ id })),
          catchError((error: HttpErrorResponse) => of(deleteCustomFieldDefinitionError({ error }))),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private service: CustomFieldDefinitionService,
  ) {}
}
